import React from 'react';
import BookAppointmentLink from './BookAppointmentLink';
import { trackPhoneClick } from '../utils/analytics';

const Hero: React.FC = () => {
  // Add state to track if it's mobile view for video autoplay
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section id="hero" className="bg-white relative overflow-hidden">
      {/* Mobile Layout - Function Health Style (Overlay Text) */}
      <div className="block lg:hidden bg-white">
        <div className="px-4 pt-2 pb-8">
          {/* Spacer */}
          <div className="h-1" aria-hidden="true"></div>

          {/* Immersive Card Container */}
          <div className="relative w-full shadow-xl overflow-hidden" style={{borderRadius: '1.5rem', aspectRatio: '4/5'}}>
            {/* Background Video */}
            <video 
              src="/chomphome.mp4?v=2" 
              autoPlay={!isMobile}
              loop
              muted
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
              poster="/homeimagebouviercat.png"
              aria-label="Decorative background video of pets at CHOMP Pet Dental (no sound)"
            >
              <source src="/chomphome.mp4?v=2" type="video/mp4" />
              <img 
                src="/homeimagebouviercat.png" 
                alt="Professional pet dental care" 
                className="w-full h-full object-cover object-center"
              />
            </video>
            
            {/* Dark Gradient Overlay for Text Readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60 pointer-events-none"></div>

            {/* Content Overlay */}
            <div className="absolute inset-0 flex flex-col justify-between p-6 z-10">
              {/* Top Text */}
              <div className="text-center pt-4">
                <span className="text-white/90 text-sm font-semibold tracking-widest uppercase mb-2 block font-founders drop-shadow-md">Veterinary Dentistry</span>
                <h1 className="text-white text-3xl font-bold leading-tight font-nantes drop-shadow-lg">
                  The Future of <br/>Pet Dental Care
                </h1>
              </div>

              {/* Bottom Action */}
              <div className="w-full pb-4">
                <BookAppointmentLink 
                  analyticsSource="hero"
                  className="w-full inline-flex items-center justify-center bg-hellotend-brand-brown text-white px-6 py-4 text-lg font-bold font-founders hover:bg-opacity-90 transition-all duration-300 shadow-lg transform active:scale-[0.98] backdrop-blur-sm"
                  style={{borderRadius: '2rem'}}
                >
                  Book Now
                </BookAppointmentLink>
                <p className="text-center text-xs text-white/80 mt-3 font-founders font-medium drop-shadow-md">
                  Fort Lauderdale clinic • Serving South Florida
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:block min-h-screen">
        <div className="max-w-[1400px] mx-auto px-12 h-full">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-12 items-center h-full py-16">
            {/* Left Side - Text Content */}
            <div className="space-y-8 animate-fade-in">
              <div className="space-y-6">
                <h1
                  className="text-4xl lg:text-5xl xl:text-6xl font-bold text-hellotend-neutral-800 leading-[1.1] font-nantes tracking-tight"
                  aria-label="The First Veterinary Clinic 100% Dedicated to Pet Dental Care"
                >
                  <span aria-hidden="true">
                    <span className="text-2xl lg:text-3xl block mb-2">The First Veterinary Clinic</span>
                    100% Dedicated to Pet Dental Care
                  </span>
                </h1>
                  
                <p className="text-xl text-hellotend-neutral-700 font-founders max-w-2xl leading-relaxed">
                  Professional dental care under anesthesia by veterinarians who do just one thing: keep your pet's mouth healthy for life.
                </p>
              </div>
              
              <div className="flex flex-row items-center gap-6 relative z-10">
                <BookAppointmentLink 
                  analyticsSource="hero"
                  className="inline-flex items-center justify-center bg-hellotend-brand-blue text-white px-8 py-4 text-lg font-medium font-founders hover:bg-blue-700 transition-colors duration-300 rounded-3xl"
                >
                  Book now
                </BookAppointmentLink>
                <a
                  href="tel:+19544663775"
                  onClick={() => trackPhoneClick('hero')}
                  className="flex items-center justify-center gap-3 text-hellotend-neutral-700 border-2 border-gray-300 px-6 py-4 hover:border-gray-400 hover:bg-gray-50 transition-all duration-300 rounded-3xl cursor-pointer relative z-10"
                  aria-label="Call CHOMP at (954) 466-3775"
                >
                  <i className="fas fa-phone text-base" aria-hidden="true"></i>
                  <span className="text-lg font-medium font-founders">(954) 466-3775</span>
                </a>
              </div>
              
              {/* Trust indicators */}
              <div className="flex flex-col gap-3 pt-6 border-t border-hellotend-neutral-200">
                <div className="flex items-center gap-3">
                  <i className="fas fa-shield-alt text-hellotend-brand-blue text-sm"></i>
                  <span className="text-base text-hellotend-neutral-600 font-normal font-founders">
                    Higher Quality & Safer Dental Work
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fas fa-dollar-sign text-hellotend-brand-blue text-sm"></i>
                  <span className="text-base text-hellotend-neutral-600 font-normal font-founders">
                    More Affordable
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fas fa-clock text-hellotend-brand-blue text-sm"></i>
                  <span className="text-base text-hellotend-neutral-600 font-normal font-founders">
                    Faster! In One Visit
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right Side - Hero Image */}
            <div className="relative animate-slide-up -mr-8 h-[80vh]">
              <div className="relative overflow-hidden w-full h-full shadow-2xl rounded-3xl">
                <video 
                  src="/chomphome.mp4" 
                  autoPlay={!isMobile}
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover object-center rounded-3xl pointer-events-none"
                  poster="/homeimagebouviercat.png"
                  aria-label="Decorative background video of pets at CHOMP Pet Dental (no sound)"
                >
                  <source src="/chomphome.mp4" type="video/mp4" />
                  <img 
                    src="/homeimagebouviercat.png" 
                    alt="Professional pet dental care for dogs and cats in Fort Lauderdale" 
                    loading="lazy"
                    width="1200"
                    height="630"
                    className="w-full h-full object-cover object-center rounded-3xl"
                  />
                </video>
              </div>
            </div>
          </div>
          
          {/* Down Arrow */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <i className="fas fa-chevron-down text-2xl text-hellotend-neutral-400"></i>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
